import { featureToCharger, type ChargerFC } from '@/lib/api';
import type { Charger } from '@/types/charger';

export function bearingDeg(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const p1 = (lat1 * Math.PI) / 180;
  const p2 = (lat2 * Math.PI) / 180;
  const dl = ((lng2 - lng1) * Math.PI) / 180;
  const y = Math.sin(dl) * Math.cos(p2);
  const x = Math.cos(p1) * Math.sin(p2) - Math.sin(p1) * Math.cos(p2) * Math.cos(dl);
  return ((Math.atan2(y, x) * 180) / Math.PI + 360) % 360;
}

export function haversineM(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const r = 6371000;
  const p1 = (lat1 * Math.PI) / 180;
  const p2 = (lat2 * Math.PI) / 180;
  const dp = ((lat2 - lat1) * Math.PI) / 180;
  const dl = ((lng2 - lng1) * Math.PI) / 180;
  const a = Math.sin(dp / 2) ** 2 + Math.cos(p1) * Math.cos(p2) * Math.sin(dl / 2) ** 2;
  return 2 * r * Math.asin(Math.min(1, Math.sqrt(a)));
}

export function formatDistanceM(meters: number): string {
  if (meters < 950) return `${Math.round(meters / 10) * 10} m`;
  const km = meters / 1000;
  if (km < 10) return `${km.toFixed(1).replace('.', ',')} km`;
  return `${Math.round(km)} km`;
}

export function formatDurationS(seconds: number): string {
  const m = Math.max(1, Math.round(seconds / 60));
  if (m < 60) return `${m} perc`;
  const h = Math.floor(m / 60);
  const rest = m % 60;
  return rest ? `${h} óra ${rest} perc` : `${h} óra`;
}

export function formatPrice(c: Charger): string | null {
  if (c.priceKwh != null && Number.isFinite(c.priceKwh) && c.priceKwh > 0) {
    return `${Math.round(c.priceKwh)} Ft/kWh`;
  }
  const cost = (c.usageCost || '').trim();
  return cost !== '' ? cost : null;
}

export type NearbyCharger = Charger & { distanceM: number };

export function nearestChargers(fc: ChargerFC, lat: number, lng: number, limit = 5, minSepM = 80): NearbyCharger[] {
  const ranked = fc.features
    .map((f) => {
      const c = featureToCharger(f);
      const [clng, clat] = c.coordinates;
      return { ...c, distanceM: haversineM(lat, lng, clat, clng) };
    })
    .filter((c) => Number.isFinite(c.distanceM) && c.id > 0)
    .sort((a, b) => a.distanceM - b.distanceM);
  const picked: NearbyCharger[] = [];
  for (const row of ranked) {
    const [lng0, lat0] = row.coordinates;
    if (picked.some((p) => haversineM(lat0, lng0, p.coordinates[1], p.coordinates[0]) < minSepM)) {
      continue;
    }
    picked.push(row);
    if (picked.length >= limit) break;
  }
  return picked;
}

/**
 * Perpendicular distance (meters) from point P to the segment A-B, plus how far
 * along the segment (0..1) the closest point falls. Uses a local equirectangular
 * projection around A, accurate enough at route-planner buffer scales (a few km).
 */
export function pointToSegmentM(
  latP: number,
  lngP: number,
  latA: number,
  lngA: number,
  latB: number,
  lngB: number
): { distanceM: number; t: number } {
  const r = 6371000;
  const rad = Math.PI / 180;
  const x = (lng: number, lat: number) => (lng - lngA) * rad * Math.cos((latA * rad)) * r;
  const y = (lat: number) => (lat - latA) * rad * r;
  const ax = 0;
  const ay = 0;
  const bx = x(lngB, latB);
  const by = y(latB);
  const px = x(lngP, latP);
  const py = y(latP);
  const abLenSq = bx * bx + by * by;
  let t = abLenSq > 0 ? (px * bx + py * by) / abLenSq : 0;
  t = Math.max(0, Math.min(1, t));
  const cx = ax + t * (bx - ax);
  const cy = ay + t * (by - ay);
  const distanceM = Math.hypot(px - cx, py - cy);
  return { distanceM, t };
}

export type RouteCharger = Charger & { distanceFromRouteM: number; progressM: number };

/** Returns the distance travelled along a route at the point closest to the supplied coordinate. */
export function progressAlongRouteM(coords: [number, number][], lat: number, lng: number): number {
  if (coords.length < 2) return 0;

  let cumulativeM = 0;
  let closestDistanceM = Infinity;
  let closestProgressM = 0;
  for (let i = 1; i < coords.length; i++) {
    const [lngA, latA] = coords[i - 1];
    const [lngB, latB] = coords[i];
    const segmentM = haversineM(latA, lngA, latB, lngB);
    const candidate = pointToSegmentM(lat, lng, latA, lngA, latB, lngB);
    if (candidate.distanceM < closestDistanceM) {
      closestDistanceM = candidate.distanceM;
      closestProgressM = cumulativeM + candidate.t * segmentM;
    }
    cumulativeM += segmentM;
  }
  return closestProgressM;
}

/**
 * Filters `fc` down to chargers within `bufferM` of the polyline `coords`
 * ([lng,lat][], the OSRM route geometry), sorted by cumulative distance-along-route
 * so the results read in route order (nearest match per charger, not every segment).
 */
export function chargersAlongRoute(
  fc: ChargerFC,
  coords: [number, number][],
  bufferM = 2000
): RouteCharger[] {
  if (coords.length < 2) return [];
  const cumM: number[] = [0];
  for (let i = 1; i < coords.length; i++) {
    cumM.push(cumM[i - 1] + haversineM(coords[i - 1][1], coords[i - 1][0], coords[i][1], coords[i][0]));
  }
  const out: RouteCharger[] = [];
  for (const f of fc.features) {
    const c = featureToCharger(f);
    const [clng, clat] = c.coordinates;
    let best = Infinity;
    let bestProgressM = 0;
    for (let i = 1; i < coords.length; i++) {
      const [lngA, latA] = coords[i - 1];
      const [lngB, latB] = coords[i];
      const { distanceM, t } = pointToSegmentM(clat, clng, latA, lngA, latB, lngB);
      if (distanceM < best) {
        best = distanceM;
        bestProgressM = cumM[i - 1] + t * (cumM[i] - cumM[i - 1]);
      }
    }
    if (best <= bufferM && c.id > 0) {
      out.push({ ...c, distanceFromRouteM: best, progressM: bestProgressM });
    }
  }
  return out.sort((a, b) => a.progressM - b.progressM);
}
